import { saveAs } from 'file-saver';
import { jsPDF } from 'jspdf';

export const exportFormats = {
  JSON: 'json',
  CSV: 'csv',
  PDF: 'pdf'
};

export const downloadPersonas = (personas, format) => {
  switch (format) {
    case exportFormats.JSON:
      const jsonContent = JSON.stringify(personas, null, 2);
      const jsonBlob = new Blob([jsonContent], { 
        type: 'application/json;charset=utf-8'
      });
      saveAs(jsonBlob, 'personas.json');
      break;

    case exportFormats.CSV:
      let csvContent = 'Name,Follower Base %,Description,Intro Narrative,Full Name,Job Title,Age Range,Gender,Lifestyle Factors,Income Level,Education Level,Career Trajectory,Daily Responsibilities,Success Metrics,Tools & Platforms,Industry,Experience,Skills,Primary Goal,Secondary Objective,Key Challenges,Urgency Level,Current Workarounds,Role,Evaluation Criteria,Research Methods,Key Objections,Other Stakeholders,Preferred Channels,Content Types,Language Style,Events & Communities,How It Helps,Key Benefits,Valuable Features,Messaging Angle,Best Channels,Content Topics,Call to Action,Objections to Prepare For\n';
      
      personas.forEach(persona => {
        csvContent += [
          persona.name,
          persona.follower_base_percentage,
          persona.description,
          persona.intro_narrative,
          persona.demographics.full_name,
          persona.demographics.job_title,
          persona.demographics.age_range,
          persona.demographics.gender,
          persona.demographics.lifestyle_factors,
          persona.demographics.income_level,
          persona.demographics.education_level,
          persona.personal_context.career_trajectory,
          persona.personal_context.daily_responsibilities,
          persona.personal_context.success_metrics,
          persona.personal_context.tools_platforms_used.join('; '),
          persona.professional_background.industry,
          persona.professional_background.experience,
          persona.professional_background.skills.join('; '),
          persona.goals_challenges.primary_goal,
          persona.goals_challenges.secondary_objective,
          persona.goals_challenges.key_challenges,
          persona.goals_challenges.urgency_level,
          persona.goals_challenges.current_workarounds.join('; '),
          persona.decision_making_process.role,
          persona.decision_making_process.evaluation_criteria.join('; '),
          persona.decision_making_process.research_methods.join('; '),
          persona.decision_making_process.key_objections,
          persona.decision_making_process.other_stakeholders,
          persona.communication_preferences.preferred_channels.join('; '),
          persona.communication_preferences.content_types.join('; '),
          persona.communication_preferences.language_style,
          persona.communication_preferences.events_communities.join('; '),
          persona.value_proposition.how_it_helps,
          persona.value_proposition.key_benefits.join('; '),
          persona.value_proposition.valuable_features.join('; '),
          persona.value_proposition.messaging_angle,
          persona.engagement_strategy.best_channels.join('; '),
          persona.engagement_strategy.content_topics.join('; '),
          persona.engagement_strategy.call_to_action,
          persona.engagement_strategy.objections_to_prepare_for
        ].map(field => `"${field?.toString().replace(/"/g, '""')}"`).join(',') + '\n';
      });
      
      const csvBlob = new Blob(['\ufeff' + csvContent], { 
        type: 'text/csv;charset=utf-8;' 
      });
      saveAs(csvBlob, 'personas.csv');
      break;

    case exportFormats.PDF:
      const doc = new jsPDF();
      let yPos = 10;
      const pageHeight = doc.internal.pageSize.height;
      const margin = 10;
      const lineHeight = 7;

      personas.forEach((persona, index) => {
        if (yPos > pageHeight - margin) {
          doc.addPage();
          yPos = margin;
        }

        // Add persona details
        doc.setFontSize(16);
        doc.text(persona.name, margin, yPos);
        yPos += lineHeight;

        doc.setFontSize(12);
        doc.text(`Follower Base: ${persona.follower_base_percentage}%`, margin, yPos);
        yPos += lineHeight;

        doc.text(`Description: ${persona.description}`, margin, yPos, { maxWidth: 180 });
        yPos += calculateTextHeight(doc, persona.description, 180) + lineHeight;

        // Add sections
        addSection(doc, 'Demographics', persona.demographics, margin, yPos);
        yPos += calculateSectionHeight(doc, persona.demographics) + lineHeight;

        addSection(doc, 'Personal Context', persona.personal_context, margin, yPos);
        yPos += calculateSectionHeight(doc, persona.personal_context) + lineHeight;

        addSection(doc, 'Professional Background', persona.professional_background, margin, yPos);
        yPos += calculateSectionHeight(doc, persona.professional_background) + lineHeight;

        if (yPos > pageHeight - 60) {
          doc.addPage();
          yPos = margin;
        }

        addSection(doc, 'Goals & Challenges', persona.goals_challenges, margin, yPos);
        yPos += calculateSectionHeight(doc, persona.goals_challenges) + lineHeight;

        addSection(doc, 'Decision Making Process', persona.decision_making_process, margin, yPos);
        yPos += calculateSectionHeight(doc, persona.decision_making_process) + lineHeight;

        if (yPos > pageHeight - 60) {
          doc.addPage();
          yPos = margin;
        }

        addSection(doc, 'Communication Preferences', persona.communication_preferences, margin, yPos);
        yPos += calculateSectionHeight(doc, persona.communication_preferences) + lineHeight;

        addSection(doc, 'Value Proposition', persona.value_proposition, margin, yPos);
        yPos += calculateSectionHeight(doc, persona.value_proposition) + lineHeight;

        addSection(doc, 'Engagement Strategy', persona.engagement_strategy, margin, yPos);
        yPos += calculateSectionHeight(doc, persona.engagement_strategy) + lineHeight;

        // Add page break between personas
        if (index < personas.length - 1) {
          doc.addPage();
          yPos = margin;
        }
      });

      doc.save('personas.pdf');
      break;

    default:
      console.error('Invalid export format');
  }
};

const calculateTextHeight = (doc, text, maxWidth) => {
  const lines = doc.splitTextToSize(text, maxWidth);
  return lines.length * 7;
};

const calculateSectionHeight = (doc, section) => {
  let height = 0;
  Object.entries(section).forEach(([key, value]) => {
    const text = Array.isArray(value) ? value.join(', ') : value.toString();
    height += calculateTextHeight(doc, text, 180) + 7;
  });
  return height;
};

const addSection = (doc, title, data, x, y) => {
  let currentY = y;
  doc.setFontSize(14);
  doc.text(title, x, currentY);
  currentY += 7;

  doc.setFontSize(12);
  Object.entries(data).forEach(([key, value]) => {
    const formattedKey = key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    const text = Array.isArray(value) ? value.join(', ') : value.toString();
    doc.text(`${formattedKey}: ${text}`, x, currentY, { maxWidth: 180 });
    currentY += calculateTextHeight(doc, text, 180) + 7;
  });
};
